/**
 * @file app/paymentModules/npipe/npipeClient.js
 *
 * NPIPE (Novopayment NPIPE Listener, spec v1.1.6) client: terminal configuration
 * stored in AsyncStorage `novoSettings` (legacy keys kept), paired terminals with
 * a default and a per-module override, and every action the integration needs.
 * Amounts handed to this module are MINOR units (e.g. fillér), as the spec wants.
 * Actions never throw: they resolve with a normalised result whose `error` is set
 * on failure (`error.kind`: config | transport | auth | request | timeout |
 * declined | cancelled | unsupported).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { getNovoTerminalSecret, NOVO_MERCHANT_REFERENCE } from '../../tauri/integrationsConfig';
import { NpipeTransportError, postSigned } from './npipeTransport';

const STORAGE_KEY = 'novoSettings';
export const NPIPE_MODULE_KEYS = ['pos', 'driver', 'service'];
export const SOFTPOS_HOST = '127.0.0.1';
const DEFAULT_PORT = 8081; // spec default listening_port
const DEFAULT_TIMEOUT_MS = 90000;
const TRANSPORT_MARGIN_MS = 15000;
const PTTP_TYPES = ['Sca', 'POSitive', 'Wallee', 'EVO'];

const ACTIONS = {
    purchase: 'npipe.action.purchase',
    reversal: 'npipe.action.reversal',
    refund: 'npipe.action.refund',
    endOfDay: 'npipe.action.endofday',
    connectionTest: 'npipe.action.testconnect',
    reprint: 'npipe.action.reprint_receipt',
    abort: 'npipe.action.abort',
    display: 'npipe.action.display',
    cancelDisplay: 'npipe.action.cancel_display',
};

// ---------------------------------------------------------------- storage

const readSettings = async () => {
    try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_e) {
        return {};
    }
};

const writeSettings = async (settings) => {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    return settings;
};

const devicesOf = (settings) => (Array.isArray(settings.novoPairedDevices) ? settings.novoPairedDevices : []);

const normalizePttp = (value) => {
    const found = PTTP_TYPES.find((p) => p.toLowerCase() === String(value || '').toLowerCase());
    return found || 'Sca';
};

const toTimeoutMs = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) {
        return DEFAULT_TIMEOUT_MS;
    }
    return n < 1000 ? n * 1000 : n; // small values were entered as seconds
};

/**
 * Merge values into AsyncStorage `novoSettings` (paired devices and other keys are kept).
 * @param {Object} patch legacy/new keys, e.g. { novoIpAddress, novoPttpType, novoTipEnabled }
 * @returns {Promise<Object>} the merged settings object
 */
export async function saveNpipeSettings(patch = {}) {
    const current = await readSettings();
    return writeSettings({ ...current, ...patch });
}

/**
 * List the saved (paired) terminals.
 * @returns {Promise<Array<Object>>} devices { id, name, host, port, secret, pttpType, merchantReference, mode }
 */
export async function listPairedDevices() {
    return devicesOf(await readSettings());
}

/**
 * Create or update a paired terminal. The first device becomes the default.
 * @param {Object} device { id?, name, host, port, secret, pttpType, merchantReference, mode? }
 * @returns {Promise<Object>} the stored device (with id)
 */
export async function upsertPairedDevice(device = {}) {
    const settings = await readSettings();
    const list = devicesOf(settings);
    const id = device.id || `npd_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
    const mode = device.mode === 'softpos' ? 'softpos' : 'terminal';
    const stored = {
        id,
        name: String(device.name || '').trim(),
        mode,
        host: mode === 'softpos' ? SOFTPOS_HOST : String(device.host || '').trim(),
        port: Number(device.port) || DEFAULT_PORT,
        secret: String(device.secret || ''),
        pttpType: normalizePttp(device.pttpType),
        merchantReference: String(device.merchantReference || '').trim(),
    };
    const exists = list.some((d) => d.id === id);
    const next = exists ? list.map((d) => (d.id === id ? { ...d, ...stored } : d)) : [...list, stored];
    const patch = { ...settings, novoPairedDevices: next };
    if (!patch.novoDefaultDeviceId) {
        patch.novoDefaultDeviceId = id;
    }
    await writeSettings(patch);
    return next.find((d) => d.id === id);
}

/**
 * Delete a paired terminal and clear any default / module reference to it.
 * @param {string} id device id
 * @returns {Promise<void>}
 */
export async function removePairedDevice(id) {
    const settings = await readSettings();
    const list = devicesOf(settings).filter((d) => d.id !== id);
    const modules = { ...(settings.novoModuleDevices || {}) };
    Object.keys(modules).forEach((k) => {
        if (modules[k] === id) {
            delete modules[k];
        }
    });
    await writeSettings({
        ...settings,
        novoPairedDevices: list,
        novoModuleDevices: modules,
        novoDefaultDeviceId: settings.novoDefaultDeviceId === id ? (list[0] ? list[0].id : null) : settings.novoDefaultDeviceId,
    });
}

/**
 * Choose the default terminal used when a module has no override.
 * @param {string|null} id device id (null clears)
 * @returns {Promise<void>}
 * @throws {Error} unknown device id
 */
export async function setDefaultDevice(id) {
    const settings = await readSettings();
    if (id && !devicesOf(settings).some((d) => d.id === id)) {
        throw new Error('Unknown NPIPE device');
    }
    await writeSettings({ ...settings, novoDefaultDeviceId: id || null });
}

/**
 * Set (or clear with a falsy id) the terminal used by one module.
 * @param {'pos'|'driver'|'service'} moduleKey module
 * @param {string|null} id device id
 * @returns {Promise<void>}
 * @throws {Error} bad module key / unknown device id
 */
export async function setModuleDevice(moduleKey, id) {
    if (!NPIPE_MODULE_KEYS.includes(moduleKey)) {
        throw new Error(`Unknown NPIPE module "${moduleKey}"`);
    }
    const settings = await readSettings();
    if (id && !devicesOf(settings).some((d) => d.id === id)) {
        throw new Error('Unknown NPIPE device');
    }
    const modules = { ...(settings.novoModuleDevices || {}) };
    if (id) {
        modules[moduleKey] = id;
    } else {
        delete modules[moduleKey];
    }
    await writeSettings({ ...settings, novoModuleDevices: modules });
}

/**
 * Resolve the terminal for a module: per-module device, else default device,
 * else the legacy single-terminal settings (novoIpAddress ...).
 * @param {'pos'|'driver'|'service'} [moduleKey] module asking for a terminal
 * @returns {Promise<null|{mode:'terminal'|'softpos', host:string, port:number, secret:string, pttpType:string, merchantReference:string, currency:string, timeoutMs:number, tipEnabled:boolean, printout:number, deviceId:string|null, deviceName:string}>} config or null when none is usable
 */
export async function loadNpipeConfig(moduleKey) {
    const s = await readSettings();
    const list = devicesOf(s);
    const modules = s.novoModuleDevices || {};
    const device =
        (moduleKey && list.find((d) => d.id === modules[moduleKey])) ||
        list.find((d) => d.id === s.novoDefaultDeviceId) ||
        null;

    const src = device || {
        mode: s.novoMode,
        host: s.novoIpAddress,
        port: s.novoPortNumber,
        secret: s.novoSecret,
        pttpType: s.novoPttpType,
        merchantReference: s.novoMerchantReference,
    };
    const mode = src.mode === 'softpos' ? 'softpos' : 'terminal';
    const host = mode === 'softpos' ? SOFTPOS_HOST : String(src.host || '').trim();
    if (!host) {
        return null;
    }
    return {
        mode,
        host,
        port: Number(src.port) || DEFAULT_PORT,
        secret: getNovoTerminalSecret(src.secret || s.novoSecret || undefined),
        pttpType: normalizePttp(src.pttpType || s.novoPttpType),
        merchantReference: String(src.merchantReference || s.novoMerchantReference || NOVO_MERCHANT_REFERENCE || ''),
        currency: String(s.novoCurrency || 'HUF').toUpperCase(),
        timeoutMs: toTimeoutMs(s.novoTimeout),
        tipEnabled: Boolean(s.novoTipEnabled),
        printout: s.novoPrintout === false ? 1 : 2, // SCA: 2 = host app prints (receipt data returned), 1 = terminal prints
        deviceId: device ? device.id : null,
        deviceName: device ? device.name : '',
    };
}

// ---------------------------------------------------------- normalisation

const num = (v) => {
    if (v === undefined || v === null || v === '') {
        return null;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

/** Strip the SCA "sca.<action>." prefix of response keys. */
const stripScaPrefix = (params) => {
    const out = {};
    Object.keys(params || {}).forEach((k) => {
        out[k.replace(/^sca\.[A-Za-z_]+\./, '')] = params[k];
    });
    return out;
};

/** SCA printout / EOD data: object, or array of single-key objects, -> ordered [{key,value}]. */
const toKeyValueLines = (value) => {
    if (!value || typeof value !== 'object') {
        return [];
    }
    if (Array.isArray(value)) {
        const lines = [];
        value.forEach((item) => {
            if (item && typeof item === 'object' && typeof item.content !== 'string') {
                Object.keys(item).forEach((k) => lines.push({ key: k, value: String(item[k]) }));
            }
        });
        return lines;
    }
    return Object.keys(value).map((k) => ({ key: k, value: String(value[k]) }));
};

const normalizeReceipts = (value) => {
    if (!Array.isArray(value)) {
        return [];
    }
    return value
        .filter((r) => r && typeof r.content === 'string')
        .map((r) => ({
            type: String(r.receiptType || '').toUpperCase() === 'CLIENT' ? 'CLIENT' : 'MERCHANT',
            format: String(r.receiptFormat || 'TEXT').toUpperCase(),
            content: r.content,
        }));
};

const EMPTY_RESULT = {
    ok: false,
    approved: false,
    responseCode: null,
    resultState: null,
    amount: null,
    amountGratuity: 0,
    cardBrand: null,
    maskedCard: null,
    slipNumber: null,
    referenceNumber: null,
    authCode: null,
    transactionDateTime: null,
    receipts: [],
    printoutLines: [],
    raw: null,
    error: null,
};

const failure = (code, message, kind, extra = {}) => ({
    ...EMPTY_RESULT,
    ...extra,
    error: { code, message, kind },
});

const SUCCESS_CODES = {
    purchase: [0, 1, 1001], // 1 / 1001 = approved with warning (EOD needed soon)
    reversal: [0, 400, 401],
    refund: [0],
    endOfDay: [0, 33, 34, 1033, 1034],
    connectionTest: [0],
    reprint: [0],
    abort: [0, 3002],
    display: [0],
    cancelDisplay: [0],
};

/**
 * Turn a raw NPIPE HTTP result into the normalised result object.
 * @param {string} kind purchase|reversal|refund|endOfDay|connectionTest|reprint|abort|display|cancelDisplay
 * @param {{status:number, bodyText:string}} http raw HTTP response
 * @param {{amount?:number}} [req] request data (used for the amount check)
 * @returns {Object} normalised result (see module docs)
 */
export function normalizeNpipeResponse(kind, http, req = {}) {
    let raw = null;
    try {
        raw = JSON.parse(http.bodyText);
    } catch (_e) {
        raw = null;
    }
    if (http.status === 401) {
        return failure(401, 'Terminal rejected the request signature (check the shared secret).', 'auth', { raw });
    }
    if (!raw || typeof raw !== 'object') {
        return failure(http.status, `Unreadable terminal response (HTTP ${http.status}).`, 'request', { raw: http.bodyText });
    }
    const rp = stripScaPrefix(raw.responseParams);
    const responseCode = num(firstDefined(raw.responseCode, raw.resultCode, rp.resultCode, rp.responseCode));
    const resultState = raw.resultState ? String(raw.resultState).toUpperCase() : null;
    const httpStatusText = String(raw.httpStatus || '').toUpperCase();
    const stateOk = resultState === null || resultState === 'SUCCESS' || (kind === 'abort' && resultState === 'CANCELED');

    const printoutLines = toKeyValueLines(rp.printout);
    const eodLines = toKeyValueLines(rp.receipts && !Array.isArray(rp.receipts) ? rp.receipts : null);
    const dataLines = toKeyValueLines(rp.data);
    const kvReceipts = Array.isArray(rp.receipts) ? toKeyValueLines(rp.receipts) : [];
    const lines = printoutLines.concat(eodLines, kvReceipts, dataLines);
    const kvMap = {};
    lines.forEach((l) => {
        kvMap[l.key] = l.value;
    });

    const codeOk = responseCode !== null && SUCCESS_CODES[kind].includes(responseCode);
    let ok = codeOk && stateOk && (httpStatusText === '' || httpStatusText === 'OK');
    if (rp.isTransApproved === false) {
        ok = false;
    }
    const cancelled = resultState === 'CANCELED' || rp.isUserCancelled === true || responseCode === 3002;
    const amount = num(firstDefined(rp.amount, rp.amountInTerminalCurrency, kvMap.AMOUNT));
    const wanted = num(req.amount);
    const dccPresent = Boolean(rp.dccText1 || rp.dccText2 || rp.terminalCurrency || rp.currencyExchangeRate);

    const result = {
        ...EMPTY_RESULT,
        ok,
        approved: ok && (kind === 'purchase' || kind === 'refund' || kind === 'reversal'),
        responseCode,
        resultState,
        amount,
        amountGratuity: num(rp.amountGratuity) || 0,
        cardBrand: firstDefined(rp.cardBrandName, kvMap.CARDTYPE) || null,
        maskedCard: firstDefined(rp.maskedCardNumber, rp.cardNumber, kvMap.CARDNO) || null,
        slipNumber: firstDefined(rp.slipNumber, kvMap.TRANID) ?? null,
        referenceNumber: firstDefined(rp.referenceNumber, rp.transactionId, kvMap.TRANID) ?? null,
        authCode: firstDefined(rp.authorisationCode, rp.authorizationCode, kvMap.AUTHCODE) || null,
        transactionDateTime: firstDefined(rp.transactionDateAndTime, rp.transactionDate && rp.transactionTime ? `${rp.transactionDate} ${rp.transactionTime}` : undefined, rp.transactionDate, kvMap.DATETIME) || null,
        receipts: normalizeReceipts(rp.receipts),
        printoutLines: lines,
        raw,
        currency: rp.currency || null,
        terminalId: firstDefined(rp.terminalId, kvMap.TID) || null,
        serverMessage: rp.serverMessage || null,
        selectedAnswer: rp.selectedAnswer ?? null,
        amountMatches: wanted !== null && amount !== null ? wanted === amount : null,
        warning: responseCode === 1 || responseCode === 1001 || responseCode === 34 || responseCode === 1034,
        dcc: dccPresent
            ? {
                present: true,
                terminalCurrency: rp.terminalCurrency || null,
                exchangeRate: num(rp.currencyExchangeRate),
                amountInCardCurrency: num(rp.amountInCardCurrency),
                text1: rp.dccText1 || null,
                text2: rp.dccText2 || null,
                exponent: rp.dccCurrencyExponent || null,
            }
            : null,
    };
    if (ok) {
        return result;
    }
    const message = String(
        firstDefined(rp.errorMessage, rp.serverMessage, kvMap.RESPMESSAGE, rp.error, httpStatusText && httpStatusText !== 'OK' ? raw.httpStatus : undefined) ||
            `Terminal returned code ${responseCode === null ? 'n/a' : responseCode}`
    );
    let errKind = 'declined';
    if (cancelled) {
        errKind = 'cancelled';
    } else if (responseCode === 408 || responseCode === 11 || responseCode === 1011) {
        errKind = 'timeout';
    } else if (http.status >= 400 || (httpStatusText && httpStatusText !== 'OK')) {
        errKind = 'request';
    }
    return { ...result, error: { code: responseCode, message, kind: errKind } };
}

// --------------------------------------------------------------- dispatch

const resolveConfig = async (opts) => {
    if (opts && opts.config) {
        // A caller-supplied config (settings screen / tools screen) may leave the secret blank: use the default.
        return { ...opts.config, secret: getNovoTerminalSecret(opts.config.secret || undefined) };
    }
    return loadNpipeConfig(opts && opts.moduleKey);
};

const run = async (kind, action, opts, build) => {
    const cfg = await resolveConfig(opts);
    if (!cfg || !cfg.host) {
        return failure('NO_CONFIG', 'No card terminal is configured.', 'config');
    }
    const built = build(cfg);
    if (built.error) {
        return failure(built.error.code, built.error.message, 'unsupported');
    }
    const timeoutMs = toTimeoutMs((opts && opts.timeoutMs) || cfg.timeoutMs) || DEFAULT_TIMEOUT_MS;
    const body = { action, pttpType: cfg.pttpType };
    if (built.withTimeout !== false) {
        body.timeout = timeoutMs;
    }
    if (built.params) {
        body.actionParams = built.params;
        if (built.withTimeout !== false) {
            body.actionParams.timeout = timeoutMs;
        }
    }
    try {
        const http = await postSigned({
            host: cfg.host,
            port: cfg.port,
            secret: cfg.secret,
            rawBody: JSON.stringify(body),
            timeoutMs: timeoutMs + TRANSPORT_MARGIN_MS,
        });
        return normalizeNpipeResponse(kind, http, built.req || {});
    } catch (e) {
        const code = e instanceof NpipeTransportError ? e.code : 'UNREACHABLE';
        // After a timeout the cardholder may still have been charged: callers must verify.
        return failure(code, String((e && e.message) || code), code === 'TIMEOUT' ? 'timeout' : 'transport', {
            uncertain: code === 'TIMEOUT' || code === 'BAD_RESPONSE',
        });
    }
};

const putIf = (target, key, value) => {
    if (value !== undefined && value !== null && value !== '') {
        target[key] = value;
    }
};

const minor = (v) => Math.round(Number(v));

const money = (cfg, p) => {
    const params = {
        amount: minor(p.amount),
        currency: String(p.currency || cfg.currency).toUpperCase(),
        merchantReference: p.merchantReference || cfg.merchantReference,
    };
    putIf(params, 'invoiceReference', p.invoiceReference || p.reference ? String(p.invoiceReference || p.reference) : undefined);
    return params;
};

const badAmount = (p) => !Number.isFinite(Number(p.amount)) || Number(p.amount) <= 0;
const printoutOf = (cfg, p) => (p.printout !== undefined ? Number(p.printout) : cfg.printout);

/**
 * Card purchase (npipe.action.purchase). Amount and tip are minor units; the tip is normally entered on the terminal and returned as amountGratuity.
 * @param {{amount:number, tip?:number, reference?:string, invoiceReference?:string, currency?:string, printout?:0|1|2, moduleKey?:string, timeoutMs?:number, config?:Object, extra?:Object}} p purchase request
 * @returns {Promise<Object>} normalised result (approved === responseCode 0)
 */
export function purchase(p = {}) {
    if (badAmount(p)) {
        return Promise.resolve(failure('BAD_AMOUNT', 'Invalid amount.', 'request'));
    }
    return run('purchase', ACTIONS.purchase, p, (cfg) => {
        const params = { ...money(cfg, p), amountGratuity: minor(p.tip) || 0 };
        if (cfg.pttpType === 'Sca') {
            params.printout = printoutOf(cfg, p);
        }
        return { params: { ...params, ...(p.extra || {}) }, req: { amount: params.amount } };
    });
}

/**
 * Reversal of a payment (npipe.action.reversal). Success is responseCode 400/401 (or 0). Wallee/EVO need slipNumber; POSitive only within 30 s.
 * @param {{amount:number, slipNumber?:number|string, referenceNumber?:string, reference?:string, invoiceReference?:string, currency?:string, printout?:0|1|2, moduleKey?:string, timeoutMs?:number, config?:Object, extra?:Object}} p reversal request
 * @returns {Promise<Object>} normalised result
 */
export function reversal(p = {}) {
    if (badAmount(p)) {
        return Promise.resolve(failure('BAD_AMOUNT', 'Invalid amount.', 'request'));
    }
    return run('reversal', ACTIONS.reversal, p, (cfg) => {
        const params = money(cfg, p);
        putIf(params, 'slipNumber', p.slipNumber !== undefined && p.slipNumber !== null && p.slipNumber !== '' ? Number(p.slipNumber) : undefined);
        putIf(params, 'referenceNumber', p.referenceNumber !== undefined && p.referenceNumber !== null ? String(p.referenceNumber) : undefined);
        if (cfg.pttpType === 'Sca') {
            params.printout = printoutOf(cfg, p);
        }
        return { params: { ...params, ...(p.extra || {}) }, req: { amount: params.amount } };
    });
}

/**
 * Refund not linked to a transaction (npipe.action.refund); not available for Sca. referenceNumber and invoiceReference are mandatory per spec.
 * @param {{amount:number, referenceNumber:string, reference?:string, invoiceReference?:string, currency?:string, moduleKey?:string, timeoutMs?:number, config?:Object, extra?:Object}} p refund request
 * @returns {Promise<Object>} normalised result
 */
export function refund(p = {}) {
    if (badAmount(p)) {
        return Promise.resolve(failure('BAD_AMOUNT', 'Invalid amount.', 'request'));
    }
    return run('refund', ACTIONS.refund, p, (cfg) => {
        if (cfg.pttpType === 'Sca') {
            return { error: { code: 'UNSUPPORTED', message: 'Refund is not available for SZEP/EP (Sca) terminals.' } };
        }
        if (p.referenceNumber === undefined || p.referenceNumber === null || p.referenceNumber === '') {
            return { error: { code: 'MISSING_REFERENCE', message: 'referenceNumber of the original payment is required.' } };
        }
        const params = money(cfg, p);
        params.referenceNumber = String(p.referenceNumber);
        return { params: { ...params, ...(p.extra || {}) }, req: { amount: params.amount } };
    });
}

/**
 * End of day / final balance (npipe.action.endofday); SCA prints via the host unless printout is overridden.
 * @param {{printout?:0|1|2, moduleKey?:string, timeoutMs?:number, config?:Object}} [p] options
 * @returns {Promise<Object>} normalised result (printoutLines hold the SCA report)
 */
export function endOfDay(p = {}) {
    return run('endOfDay', ACTIONS.endOfDay, p, (cfg) => ({
        params: cfg.pttpType === 'Sca' ? { printout: printoutOf(cfg, p) } : {},
    }));
}

/**
 * Terminal to acquirer server connection test (npipe.action.testconnect).
 * @param {{moduleKey?:string, timeoutMs?:number, config?:Object}} [p] options
 * @returns {Promise<Object>} normalised result
 */
export function connectionTest(p = {}) {
    return run('connectionTest', ACTIONS.connectionTest, p, () => ({}));
}

/**
 * Reprint the last receipt (npipe.action.reprint_receipt, Wallee only).
 * @param {{receiptType?:'TRX'|'EOD', moduleKey?:string, timeoutMs?:number, config?:Object}} [p] options
 * @returns {Promise<Object>} normalised result with receipts[]
 */
export function reprintLast(p = {}) {
    return run('reprint', ACTIONS.reprint, p, (cfg) => {
        if (cfg.pttpType !== 'Wallee') {
            return { error: { code: 'UNSUPPORTED', message: 'Receipt reprint is only available for Wallee terminals.' } };
        }
        return { params: { receiptType: p.receiptType === 'EOD' ? 'EOD' : 'TRX', receiptFormat: 1 } };
    });
}

/**
 * Abort the transaction currently waiting for a card (npipe.action.abort, Wallee/EVO).
 * @param {{isSilent?:boolean, moduleKey?:string, timeoutMs?:number, config?:Object}} [p] options
 * @returns {Promise<Object>} normalised result (ok when the terminal confirms the abort)
 */
export function abort(p = {}) {
    return run('abort', ACTIONS.abort, p, (cfg) => {
        if (cfg.pttpType !== 'Wallee' && cfg.pttpType !== 'EVO') {
            return { error: { code: 'UNSUPPORTED', message: 'Abort is only available for Wallee and EVO terminals.' } };
        }
        return { params: { isSilent: Boolean(p.isSilent) } };
    });
}

/**
 * Show text (or a question with answers) on the terminal (npipe.action.display).
 * Pass `screens` (spec display_screens object) or `text` (+ optional options for a question).
 * @param {{screens?:Object, text?:string, options?:Array<{label:string,value:number}>, durationMs?:number, backgroundColor?:string, fontColor?:string, moduleKey?:string, timeoutMs?:number, config?:Object}} p display request
 * @returns {Promise<Object>} normalised result (selectedAnswer when a question was shown)
 */
export function displayOnTerminal(p = {}) {
    return run('display', ACTIONS.display, p, () => {
        let screens = p.screens;
        if (!screens) {
            if (!p.text) {
                return { error: { code: 'BAD_REQUEST', message: 'Nothing to display.' } };
            }
            const item = {
                display_type: Array.isArray(p.options) && p.options.length ? 'ScreenWithQuestion' : 'ScreenWithText',
                display_duration: p.durationMs !== undefined ? Number(p.durationMs) : -1,
                background_color: p.backgroundColor || '#FFFFFF',
                font_color: p.fontColor || '#000000',
                html_text: String(p.text),
                display_options: (p.options || []).map((o) => ({
                    html_text: String(o.label),
                    value: Number(o.value),
                    button_color: '#000000',
                    font_color: '#ffffff',
                })),
            };
            screens = { display_items: [item] };
        }
        return { params: { display_screens: screens } };
    });
}

/**
 * Close a screen previously shown with displayOnTerminal (npipe.action.cancel_display).
 * @param {{moduleKey?:string, timeoutMs?:number, config?:Object}} [p] options
 * @returns {Promise<Object>} normalised result
 */
export function cancelDisplay(p = {}) {
    return run('cancelDisplay', ACTIONS.cancelDisplay, p, () => ({ withTimeout: false }));
}
